import type { PatientOrderingProduct } from '@customTypes';

// Returns the product ids that are required to select this product
export const getRequiredProductIds = (product: PatientOrderingProduct): string[] =>
  product.requirement_previously_met ? [] : (product.requires_product_ids ?? []);

// Returns all products that are required to select this product
export const getRequiredProducts = (
  product: PatientOrderingProduct,
  allProducts: PatientOrderingProduct[],
): PatientOrderingProduct[] => {
  const requiredProductIds = getRequiredProductIds(product);

  if (!requiredProductIds.length) {
    return [];
  }

  return allProducts.filter(p => requiredProductIds.includes(p.product_id));
};

// A product with a `requires_product_ids` dependency can only be selected once one of
// those requirements is currently selected, or was already met by a prior order.
export const canSelectProduct = (product: PatientOrderingProduct, selectedIds: string[]): boolean => {
  const requiredProductIds = getRequiredProductIds(product);

  if (!requiredProductIds.length) {
    return true;
  }

  return requiredProductIds.some(requiredProductId => selectedIds.includes(requiredProductId));
};

// Returns all product ids that should be removed if `deselectedId` is deselected, including `deselectedId`
export const getAllDeselectedProductIds = (
  products: PatientOrderingProduct[],
  selectedIds: string[],
  deselectedId: string,
): string[] => {
  const remainingIds = new Set(selectedIds);

  if (!remainingIds.has(deselectedId)) {
    return [];
  }

  remainingIds.delete(deselectedId);
  const allDeselectedIds = [deselectedId];

  let foundDependent = true;
  while (foundDependent) {
    foundDependent = false;

    for (const product of products) {
      if (!remainingIds.has(product.product_id)) {
        continue;
      }

      const requiredProductIds = getRequiredProductIds(product);
      if (!requiredProductIds.length) {
        continue;
      }

      // Only requirements this patient can order count. An unoffered id does not
      // keep the dependent selected, and it is not a reason to remove it.
      const offeredRequirementIds = requiredProductIds.filter(requiredId =>
        products.some(candidate => candidate.product_id === requiredId),
      );

      if (!offeredRequirementIds.length || offeredRequirementIds.some(requiredId => remainingIds.has(requiredId))) {
        continue;
      }

      remainingIds.delete(product.product_id);
      allDeselectedIds.push(product.product_id);
      foundDependent = true;
    }
  }

  return allDeselectedIds;
};

// Returns the product ids that should be selected by default.
// Excludes products that depend on another product or require user confirmation
export const getDefaultSelectedProductIds = (products: PatientOrderingProduct[]): string[] =>
  products
    .filter(product => !product.requires_product_ids?.length && !product.requires_confirmation)
    .map(product => product.product_id);
